import React, { useContext } from 'react';
import { Row, Col, Skeleton, Tabs, Breadcrumb, Button, Tooltip, Tag } from 'antd';
import {
  BankOutlined,
  FileTextOutlined,
  TagsOutlined,
  UnorderedListOutlined,
  CalculatorOutlined,
  CopyOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { InvoiceInfoCard } from './components/InvoiceInfoCard';
import { UserCard } from './components/UserCard';
import { RaffleCard } from './components/RaffleCard';
import { BalanceHistoryCard } from './components/BalanceHistoryCard';
import { InvoicesHistoryCard } from './components/InvoicesHistoryCard';
import { TicketsHistoryCard } from './components/TicketsHistoryCard';
import useInvoiceDetails from './useInvoiceDetails';
import { EModule } from '../../enums';
import DividerTicketsCard from './components/DividerTicketsCard';
import DividerPointsCard from './components/DividerPointsCard';
import PageHeader from '../../components/PageHeader';
import KpiCard from '../../components/KpiCard';
import StatusTag from '../CityInvoices/components/StatusTag';
import { ThemeContext } from '../../contexts/ThemeContext';
import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from '../../utils/Conversores';
import { copyToClipboard } from './utils/format';

const CardSkeleton: React.FC = () => (
  <div style={{ background: '#ffffff', borderRadius: 14, padding: 20, border: '1px solid #eaedf1' }}>
    <Skeleton active paragraph={{ rows: 5 }} />
  </div>
);

export const InvoiceDetails = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const {
    invoiceDetails,
    raffle,
    history,
    invoices,
    isLoading,
    tickets,
    onViewUser,
    onOpenInvoice,
    reloadInvoice,
    reprocessInvoice,
    isReprocessing,
  } = useInvoiceDetails();

  if (isLoading || !invoiceDetails) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Skeleton active paragraph={{ rows: 2 }} />
        <Row gutter={[16, 16]}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Col key={i} xs={24} sm={12} lg={6}>
              <div style={{ background: isDark ? '#141414' : '#ffffff', borderRadius: 14, padding: 20, border: `1px solid ${isDark ? '#262626' : '#eaedf1'}` }}>
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <CardSkeleton />
          </Col>
          <Col xs={24} lg={12}>
            <CardSkeleton />
          </Col>
        </Row>
      </div>
    );
  }

  const handleCopyJson = () => {
    copyToClipboard(JSON.stringify(invoiceDetails, null, 2), 'JSON da nota fiscal');
  };

  const tabItems = [
    {
      key: 'geral',
      label: (
        <span>
          <FileTextOutlined /> Visão Geral
        </span>
      ),
      children: (
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} lg={12}>
            <InvoiceInfoCard
              invoice={invoiceDetails}
              onReload={reloadInvoice}
              isReprocessing={isReprocessing}
              onReprocess={reprocessInvoice}
            />
          </Col>
          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <UserCard user={invoiceDetails.app_user} onViewUser={onViewUser} />
              </Col>
              <Col xs={24}>
                <RaffleCard
                  raffle={raffle}
                  belongsToThisInvoice={Boolean(invoiceDetails.tickets && invoiceDetails.tickets.length > 0)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      ),
    },
    {
      key: 'county-rules',
      label: (
        <span>
          <BankOutlined /> Regras do Município
        </span>
      ),
      children: (
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} lg={12}>
            {invoiceDetails.county?.modules?.includes(EModule.INVOICE_NFC || EModule.INVOICE_NFS) && (
              <DividerTicketsCard
                regraVigente={invoiceDetails.county?.divider_tickets}
                modules={invoiceDetails.county?.modules}
                dataNota={invoiceDetails.date_issue}
              />
            )}
          </Col>
          <Col xs={24} lg={12}>
            {invoiceDetails.county?.modules?.includes(EModule.REWARD) && (
              <DividerPointsCard
                regraVigente={invoiceDetails.county?.divider_points}
                modules={invoiceDetails.county?.modules}
                dataNota={invoiceDetails.date_issue}
              />
            )}
          </Col>
        </Row>
      ),
    },
    {
      key: 'bilhetes',
      label: (
        <span>
          <CalculatorOutlined /> Bilhetes & Cálculo
        </span>
      ),
      children: (
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24}>
            <BalanceHistoryCard history={history} />
          </Col>
        </Row>
      ),
    },
    {
      key: 'historico-usuario',
      label: (
        <span>
          <UnorderedListOutlined /> Notas & Bilhetes do Usuário
        </span>
      ),
      children: (
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24}>
            <InvoicesHistoryCard
              invoices={invoices}
              currentInvoiceId={invoiceDetails.id}
              onOpenInvoice={onOpenInvoice}
            />
          </Col>
          <Col xs={24}>
            <TicketsHistoryCard tickets={tickets} currentInvoiceId={invoiceDetails.id} />
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          background: isDark ? '#141414' : '#ffffff',
          borderRadius: 10,
          border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Breadcrumb
          items={[
            {
              title: <Link to="/notas/geral">Notas Fiscais</Link>,
            },
            {
              title: `Detalhes da Nota Fiscal #${invoiceDetails.id}`,
            },
          ]}
        />
      </div>

      <PageHeader
        icon={<FileTextOutlined />}
        title={`Nota Fiscal #${invoiceDetails.id}`}
        subtitle={
          <span>
            <strong>Empresa:</strong> {invoiceDetails.company || '--'}
            {(invoiceDetails.city_issue || invoiceDetails.state_issue) && (
              <>
                {' '}• <strong>Local de Emissão:</strong> {[invoiceDetails.city_issue, invoiceDetails.state_issue].filter(Boolean).join(' - ')}
              </>
            )}
          </span>
        }
        tag={<StatusTag status={invoiceDetails.status} message={invoiceDetails.message} />}
        actions={
          <Button
            icon={<CopyOutlined />}
            onClick={handleCopyJson}
            style={{
              borderRadius: 8,
              fontWeight: 500,
              height: 38,
            }}
          >
            Copiar JSON
          </Button>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          {invoiceDetails.type && (
            <Tag color="blue" style={{ fontWeight: 600 }}>
              Tipo: {invoiceDetails.type}
            </Tag>
          )}

          {invoiceDetails.county?.modules?.includes(EModule.INVOICE_NFC || EModule.INVOICE_NFS) && (
            <Tag color={invoiceDetails.ticket_processed ? 'green' : 'default'} style={{ fontWeight: 500 }}>
              {invoiceDetails.ticket_processed ? 'Bilhetes Processados' : 'Bilhetes não processados'}
            </Tag>
          )}

          {invoiceDetails.county?.modules?.includes(EModule.REWARD) && (
            <Tag color={invoiceDetails.points_processed ? 'green' : 'default'} style={{ fontWeight: 500 }}>
              {invoiceDetails.points_processed ? 'Pontos processados' : 'Pontos não processados'}
            </Tag>
          )}

          {invoiceDetails.process_after && (
            <Tooltip title={formatDateStringToDateAndTime(invoiceDetails.process_after)}>
              <Tag color="gold" style={{ fontWeight: 500 }}>
                Processar após: {formatDateStringToDateAndTime(invoiceDetails.process_after)}
              </Tag>
            </Tooltip>
          )}
        </div>
      </PageHeader>

      <Row gutter={[14, 14]}>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<DollarCircleOutlined style={{ fontSize: 22 }} />}
            title="Valor da Nota"
            value={`R$ ${invoiceDetails.value ? convertMoedaBRDoJSNovo(invoiceDetails.value) : '0,00'}`}
            valueFontSize={14.5}
            accentColor="#52c41a"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<TagsOutlined style={{ fontSize: 22 }} />}
            title="Bilhetes Gerados"
            value={invoiceDetails.tickets?.length || 0}
            suffix="bilhete(s)"
            valueFontSize={14.5}
            accentColor="#13c2c2"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<CalendarOutlined style={{ fontSize: 22 }} />}
            title="Data de Emissão"
            value={invoiceDetails.date_issue ? formatDateStringToDateAndTime(invoiceDetails.date_issue) : '--'}
            valueFontSize={14.5}
            accentColor="#1677ff"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<SafetyCertificateOutlined style={{ fontSize: 22 }} />}
            title="Status do Processamento"
            value={invoiceDetails.status}
            valueFontSize={14.5}
            accentColor="#fa8c16"
          />
        </Col>
      </Row>

      <div
        style={{
          background: isDark ? '#141414' : '#ffffff',
          borderRadius: 14,
          border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
          padding: '16px 20px',
        }}
      >
        <Tabs defaultActiveKey="geral" items={tabItems} />
      </div>
    </div>
  );
};

export default InvoiceDetails;